/**
 * File: traceUtils.ts
 * Path: apps/admin/src/features/diagnostics/utils/traceUtils.ts
 * Description: The live notification test's steps (components/PushTrace):
 * what the server did, whether the bell saved it, and each device's
 * confirmation with its timing. Pure, for the tests.
 */

import type { CheckStatus, PushDevice } from "@idk/config";
import { systemNotificationTip } from "@/shared/utils/notificationsUtils";

/** How long to wait for devices before saying which didn't confirm. */
export const TRACE_WAIT_MS = 60 * 1000;

export interface SendResult {
  deviceId: string;
  label: string;
  ok: boolean;
  removed: boolean;
  error?: string;
}

export interface TraceRun {
  startedAt: number;
  answeredAt: number;
  itemId: string;
  devices: SendResult[];
}

export interface TraceStep {
  id: string;
  /** Where to look if it arrived but didn't pop up. */
  tip?: string;
  status: CheckStatus | "waiting";
  text: string;
}

const seconds = (ms: number) => `${(Math.max(0, ms) / 1000).toFixed(1)} s`;

/** The steps so far. Pure, for the tests. */
export const traceSteps = (
  run: TraceRun,
  devices: readonly PushDevice[],
  savedAt: number | null,
  now: number,
): TraceStep[] => {
  const waiting = now - run.startedAt < TRACE_WAIT_MS;
  const sent = run.devices.filter((d) => d.ok);
  const steps: TraceStep[] = [
    {
      id: "server",
      status: sent.length === run.devices.length ? "ok" : sent.length > 0 ? "warn" : "fail",
      text: `Server sent it to ${sent.length} of ${run.devices.length} ${run.devices.length === 1 ? "device" : "devices"} (${seconds(run.answeredAt - run.startedAt)}).`,
    },
    savedAt !== null
      ? { id: "bell", status: "ok", text: "Saved in your bell." }
      : { id: "bell", status: waiting ? "waiting" : "fail", text: waiting ? "Saving in your bell…" : "Not found in your bell." },
  ];
  for (const result of run.devices) {
    if (!result.ok) {
      steps.push({
        id: result.deviceId,
        status: "fail",
        text: `${result.label}: not sent, ${result.removed ? "Google says this device is gone, so it was removed" : (result.error ?? "send failed")}.`,
      });
      continue;
    }
    const device = devices.find((d) => d.id === result.deviceId);
    const confirmedAt = device?.lastConfirmedAt ?? null;
    if (confirmedAt !== null && confirmedAt >= run.startedAt) {
      steps.push({
        id: result.deviceId,
        status: "ok",
        text: `${result.label}: delivered after ${seconds(confirmedAt - run.startedAt)}.`,
        tip: systemNotificationTip(result.label),
      });
    } else if (waiting) {
      steps.push({ id: result.deviceId, status: "waiting", text: `${result.label}: waiting for it to confirm…` });
    } else {
      steps.push({
        id: result.deviceId,
        status: "fail",
        text: `${result.label}: no confirmation within a minute. Google accepted it but the device didn't show it: check it's switched on, online and allows notifications.`,
      });
    }
  }
  return steps;
};
